import { Component, OnInit, Inject, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatSnackBar } from '@angular/material';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { Observable, Subject } from 'rxjs';
import { OrganizationModel } from 'src/app/organizations/models/organization.model';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { BookmarkGroupModel } from 'src/app/bookmarks/models/bookmark.group.model';
import { SaveBookmark, SaveBookmarkSuccess, SaveBookmarkFail } from 'src/app/bookmarks/state/bookmarks.actions';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { map, debounce, debounceTime } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { BookmarkEntryModel } from 'src/app/bookmarks/models/bookmark.entry.model';
import { GetCollections } from 'src/app/organizations/state/organizations.actions';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';

export enum SaveBookmarkDialogMode {
  SaveStory,
  Manage
}

export interface SaveBookmarkDialogData {
  bookmark?: BookmarkModel;
  title?: string;
  bookmarkTitle?: string;
  message?: string;
  mode?: SaveBookmarkDialogMode;
}

@Component({
  selector: 'app-save-bookmark-dialog',
  templateUrl: './save-bookmark-dialog.component.html',
  styleUrls: ['./save-bookmark-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaveBookmarkDialogComponent implements OnInit, OnDestroy {

  @Select(OrganizationsState.getOrganization)
  organization$: Observable<OrganizationModel>;

  @Select(OrganizationsState.getCollections)
  collections$: Observable<CollectionModel[]>;

  @Select(BookmarksState.getSaving)
  saving$: Observable<boolean>;

  get dialogTitle(): string {
    return this.settings.title || (this.settings.mode === SaveBookmarkDialogMode.Manage ? 'Manage' : 'Save');
  }

  form: FormGroup;
  saveDebounce = new Subject();

  settings: SaveBookmarkDialogData = {
    mode: SaveBookmarkDialogMode.SaveStory
  };

  isNew: boolean;
  isOwner: boolean;

  private currentCollection: CollectionModel;
  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SaveBookmarkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: SaveBookmarkDialogData,
    public snackBar: MatSnackBar,
    private actions: Actions,
    private organizationsService: OrganizationsService
  ) {
    
    this.settings = {
      ...this.settings,
      ...data
    };

    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(60)]],
      collection: [null],
      groupAccess: [null],
      publicAccess: [false]
    });

    // Listen for bookmark actions
    this.actions
      .pipe(
        ofActionDispatched(SaveBookmarkSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((response) => this.handleSaveSuccess(response));

    this.actions
      .pipe(
        ofActionDispatched(SaveBookmarkFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((response) => this.handleSaveFailure(response));

    this.saveDebounce
        .pipe(
          debounceTime(100),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(() => {
          this.saveBookmark();
        });
  }

  ngOnInit() {
    // populate group collections
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    if (organization) {
      this.store.dispatch(new GetCollections(organization.id));
    }

    // set form
    const bookmark = this.settings.bookmark;
    const title = bookmark ? bookmark.title : (this.settings.bookmarkTitle ? this.settings.bookmarkTitle : '');
    
    // group access state
    const groupAccess = bookmark ? this.getGroupAccess(bookmark) : null;

    let groupEdit = false;
    let groupView = false;
    if (groupAccess) { // set group access via data
      groupEdit = groupAccess.member_edit;
      groupView = !groupEdit && groupAccess.visible;
    } else if (!bookmark && organization) { // default group users
      groupEdit = false;
      groupView = true;
    }

    // public access: non-group users
    const publicAccess = !this.isGroupUser() && (bookmark && bookmark.tags.includes('public'));

    this.form.patchValue({
      title: title,
      groupAccess: groupEdit ? 'edit' : (groupView ? 'view' : ''),
      publicAccess: publicAccess
    });

    // set mode
    this.isNew = true;
    this.isOwner = true;
    if (bookmark) {
      if (bookmark.is_owner) {
        this.isNew = false;
        this.isOwner = true;
      } else {
        const isEditable = bookmark.group_access.some((access) => access.member_edit === true);
        this.isNew = !isEditable;
        this.isOwner = this.isNew;
      }
    }

    // setup group access
    this.initGroupAccess();
    
    // setup collections
    this.initCollections();

    // set group access
    this.toggleGroupAccess(groupView, groupEdit);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initGroupAccess() {
    const groupAccessField = this.form.get('groupAccess');

    // set collections: via group access
    groupAccessField.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((groupAccess) => {
        const groupView = groupAccess === 'view';
        const groupEdit = groupAccess === 'edit';
        this.toggleGroupAccess(groupView, groupEdit);
      });
  }

  initCollections() {
    const bookmark = this.settings.bookmark;
    const formCollection = this.form.get('collection');
    formCollection.disable();

    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    if (organization && bookmark) {
      const collection$ = this.organizationsService.getCollectionsByContent(organization.id, {
        content_type: bookmark.type,
        content_id: bookmark.id
      });

      collection$
        .pipe(
          map((contentCollections) => contentCollections[0]),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe((contentCollection) => {
          this.currentCollection = contentCollection;
          const currentCollectionId = this.currentCollection ? this.currentCollection.collection_id : null;
          this.form.patchValue({
            collection: currentCollectionId
          });
        });
    }
  }

  toggleGroupAccess(groupView: boolean, groupEdit: boolean) {
    const collection = this.form.get('collection');
    const canEditCollections = this.isOwner || this.isNew;
    const isGroupAccess = groupView || groupEdit;

    // clear selection
    if (!isGroupAccess) {
      collection.setValue(null);
    }
    // set enabled state   
    if (canEditCollections && isGroupAccess) {
      collection.enable();
    } else {
      collection.disable();
    }
  }

  handleSubmit() {
    if (this.form.valid) {
      this.saveDebounce.next();
    }
  }

  saveBookmark() {
    const bookmarkEntry = this.getBookmarkEntry();
    const includeIndex = this.settings.mode === SaveBookmarkDialogMode.SaveStory;
    this.store.dispatch(new SaveBookmark(bookmarkEntry, false, includeIndex));
  }
  
  handleSaveSuccess(response: SaveBookmarkSuccess) {
    const bookmark = response.bookmark;
    const message = this.settings.mode === SaveBookmarkDialogMode.Manage ?
      `Your updates to "${bookmark.title}" have been saved to your dashboard.` :
      `"${bookmark.title}" saved to your dashboard.`;

    this.snackBar.open(message);    
    this.dialogRef.close(bookmark);
  }

  handleSaveFailure(response: SaveBookmarkFail) {
    this.snackBar.open(`Unable to save bookmark "${response.bookmarkEntry.title}". Please try again.`);
  }

  private getBookmarkEntry(): BookmarkEntryModel {
    const { bookmark } = this.settings;
    const { title, collection, groupAccess, publicAccess } = this.form.value;
    const groupView = groupAccess === 'view';
    const groupEdit = groupAccess === 'edit';
    const isPublicAccess = this.isGroupUser() ? null : publicAccess;

    let addGroupAccess = [];
    let removeGroupAccess = [];
    let addCollection: CollectionModel;
    let removeCollection: CollectionModel;

    // owner: manage permissions + collections
    if (this.isOwner) {
      const groupAccessPermission = this.createGroupAccess(groupView, groupEdit);
      if (groupAccessPermission) {
        // set group access if selected
        addGroupAccess = [groupAccessPermission];
      } else {
        // remove all existing access if none selected
        removeGroupAccess = bookmark ? bookmark.group_access : [];
      }

      // handle collections
      const collections = this.store.selectSnapshot(OrganizationsState.getCollections) || [];
      const selectedCollectionId = parseInt(collection, 10);
      const selectedCollection = collections.find((c) => c.collection_id === selectedCollectionId);

      if (this.currentCollection) { // update collection
        const isCurrentSelected = this.currentCollection.collection_id === selectedCollectionId;
        if (!isCurrentSelected) { // only update when changing
          addCollection = selectedCollection;
          removeCollection = this.currentCollection;
        }
      } else if (selectedCollection) { // add optional collection
        addCollection = selectedCollection;
      }
    }

    return {
      isOwner: bookmark ? bookmark.is_owner : true,
      id: bookmark ? bookmark.id : null,
      title: title,
      add_group_access: addGroupAccess,
      remove_group_access: removeGroupAccess,
      addCollection: addCollection,
      removeCollection: removeCollection,
      public_access: isPublicAccess
    };
  }

  private getGroupAccess(bookmark: BookmarkModel): BookmarkGroupModel {
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    let groupAccess = null;
    if (organization && bookmark.group_access) {
      const organizationId = organization.id;
      groupAccess = bookmark.group_access.find((bookmarkAccessLevel) => bookmarkAccessLevel.group_id === organizationId);
    }
    return groupAccess;
  }

  private createGroupAccess(groupView: boolean, groupEdit: boolean): BookmarkGroupModel {
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    const isValidAccess = groupView || groupEdit;
    if (organization && isValidAccess) {
      return {
        group_id: organization.id,
        member_edit: groupEdit,
        visible: groupView || groupEdit
      };
    } else {
      return null;
    }
  }

  private isGroupUser(): boolean {
    return !!this.store.selectSnapshot(OrganizationsState.getOrganization);
  }

}
